import { useEffect } from 'react'
import { breadcrumb } from '../utils/sentry'

/**
 * Extracts a meaningful name from a DOM element for Sentry breadcrumbs.
 *
 * This function attempts to find a human-readable name for an element in the following order:
 * 1. Checks for the 'data-sentry-name' attribute
 * 2. Uses the element's text content (trimmed of whitespace)
 * 3. Uses the element's innerText (trimmed of whitespace) as a fallback
 *
 * @param target - The DOM element to extract the name from
 * @returns The extracted name or null if no suitable name was found
 */
export function getButtonName(target: Element): string | undefined {
    const innerText = target instanceof HTMLElement ? target.innerText : undefined
    return target.getAttribute('data-sentry-name') ?? target.textContent?.trim() ?? innerText?.trim()
}

/**
 * Tracks all button clicks on the page and sends breadcrumbs to Sentry
 *
 * This React hook adds a global click event listener to the document. When a click occurs,
 * it tries to extract a meaningful name from the clicked element using the following priority:
 * 1. 'data-sentry-name' attribute
 * 2. Text content
 * 3. innerText (for SVG and other special cases)
 *
 * If a name is successfully extracted, it sends a breadcrumb to Sentry with the format:
 * "Click: [extracted name]"
 *
 * @example
 * // Add to your root component
 * useSentryBreadcrumbsButtonName();
 *
 * // In your buttons:
 * <button>Submit</button>
 *
 * // For icons:
 * <IconButton data-sentry-name="search-icon">
 *   <SearchIcon />
 * </IconButton>
 */
export const useSentryBreadcrumbsButtonName = () => {
    useEffect(() => {
        const handleGlobalClick = (event: MouseEvent) => {
            const target = event.target
            if (!(target instanceof Element)) return
            const isContentEditable = target instanceof HTMLElement && target.contentEditable === 'true'
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || isContentEditable) {
                return
            }

            const name = getButtonName(target)
            if (name) breadcrumb('click', `Click: ${name}`)
        }

        document.addEventListener('click', handleGlobalClick)
        return () => document.removeEventListener('click', handleGlobalClick)
    }, [])
}
